import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ensureImageCached,
  getCachedBlobUrl,
  getCachedImageStatus,
  getCachedImageUrl,
  markImageFailed,
  markImageLoaded,
  subscribeToImageCache,
  ZoomMeta,
} from '../../hooks/useZoomPreview';

export interface ProductImagePreviewProps {
  imageUrl?: string | null;
  alt: string;
  size: number;
  borderColor?: string;
  borderRadius?: number | string;
  meta?: ZoomMeta;
  testId?: string;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}

export const ProductImagePreview: React.FC<ProductImagePreviewProps> = ({
  imageUrl,
  alt,
  size,
  borderColor = 'var(--border)',
  borderRadius = 'var(--radius-sm)',
  meta,
  testId,
  onImageHover,
  onImageMove,
  onImageLeave,
}) => {
  const [imgError, setImgError] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => (imageUrl ? getCachedImageUrl(imageUrl) : ''));

  const handleImgError = useCallback(() => {
    if (!imageUrl || getCachedImageStatus(imageUrl) === 'pending') {
      return;
    }
    markImageFailed(imageUrl);
    setImgError(true);
  }, [imageUrl]);

  const handleImgLoad = useCallback(() => {
    if (!imageUrl) return;
    markImageLoaded(imageUrl);
    setImgError(false);
  }, [imageUrl]);

  useEffect(() => {
    if (!imageUrl) {
      setImageSrc('');
      setImgError(true);
      return;
    }

    setImageSrc(getCachedBlobUrl(imageUrl) ?? imageUrl);
    setImgError(getCachedImageStatus(imageUrl) === 'failed' && !getCachedBlobUrl(imageUrl));
    ensureImageCached(imageUrl);

    const unsubscribe = subscribeToImageCache(imageUrl, () => {
      const blobUrl = getCachedBlobUrl(imageUrl);
      if (blobUrl) {
        setImageSrc(blobUrl);
        setImgError(false);
      } else if (getCachedImageStatus(imageUrl) === 'failed') {
        setImgError(true);
      }
    });

    return unsubscribe;
  }, [imageUrl]);

  const canZoom = Boolean(imageUrl && !imgError);

  return (
    <span
      data-testid={testId}
      style={{
        width: size,
        height: size,
        borderRadius,
        overflow: 'hidden',
        background: '#000',
        border: `1px solid ${borderColor}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
      onMouseEnter={
        canZoom && onImageHover && imageUrl
          ? (event) => onImageHover(event, imageUrl, meta)
          : undefined
      }
      onMouseMove={
        canZoom && onImageMove && imageUrl
          ? (event) => onImageMove(event, imageUrl, meta)
          : undefined
      }
      onMouseLeave={canZoom ? onImageLeave : undefined}
    >
      {imageUrl && !imgError ? (
        <img
          src={imageSrc}
          alt={alt}
          draggable={false}
          onLoad={handleImgLoad}
          onError={handleImgError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            background: '#000',
          }}
        />
      ) : (
        <BrokenImageOutlinedIcon
          style={{ fontSize: Math.max(14, size * 0.42), color: 'var(--text-tertiary)' }}
        />
      )}
    </span>
  );
};
